import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api, deleteClips } from '../api';
import { ClipGalleryItem } from './ClipGalleryPage'; // Reuse the type
import ClipCard from '../components/clips/ClipCard';
import ClipDetailPopup from '../components/clips/ClipDetailPopup';
import DeleteConfirmDialog from '../components/ui/DeleteConfirmDialog';
import { ArrowLeft, Film, Sparkles, Edit3, Trash2, CheckSquare, Square, Video, Wand2, PlaySquare, Scissors } from 'lucide-react';

const ClipListPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [clips, setClips] = useState<ClipGalleryItem[]>([]);
  const [vodInfo, setVodInfo] = useState<{ title: string | null, thumbnailUrl: string | null }>({ title: null, thumbnailUrl: null });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClip, setSelectedClip] = useState<ClipGalleryItem | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  // Eğer URL state'inde fromCreations varsa edited filtresini aç
  const shouldShowEdited = location.state?.fromCreations || location.state?.category === 'edited';
  const [activeCategory, setActiveCategory] = useState<'original' | 'edited'>(shouldShowEdited ? 'edited' : 'original');
  
  // Edit mode and selection states
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedClipIds, setSelectedClipIds] = useState<string[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fetchClipsForJob = useCallback(async () => {
    if (!jobId) return;
    setIsLoading(true);
    setError(null);
    try {
      // This endpoint fetches all clips for a specific job
      const response = await api.get<ClipGalleryItem[]>(`/clips/job/${jobId}`);
      const fetchedClips = response.data;
      setClips(fetchedClips);
      if (fetchedClips.length > 0) {
        setVodInfo({
          title: fetchedClips[0].vodTitle,
          thumbnailUrl: fetchedClips[0].vodThumbnailUrl,
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load clips for this VOD.');
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchClipsForJob();
  }, [fetchClipsForJob]);

  const handleClipClick = (clip: ClipGalleryItem) => {
    if (!isEditMode) {
      setSelectedClip(clip);
      setIsPopupOpen(true);
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedClip(null);
  };

  const handleNavigate = (newIndex: number) => {
    const filteredClips = clips.filter(clip => clip.type === activeCategory);
    if (newIndex >= 0 && newIndex < filteredClips.length) {
      setSelectedClip(filteredClips[newIndex]);
    }
  };

  // Edit mode functions
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    setSelectedClipIds([]);
  };

  const handleClipSelection = (clipId: string) => {
    setSelectedClipIds(prev => 
      prev.includes(clipId) 
        ? prev.filter(id => id !== clipId)
        : [...prev, clipId]
    );
  };

  const selectAllClips = () => {
    const allCurrentClipIds = displayedClips.map(clip => clip.id);
    setSelectedClipIds(allCurrentClipIds);
  };

  const deselectAllClips = () => {
    setSelectedClipIds([]);
  };

  const handleDeleteClick = () => {
    if (selectedClipIds.length > 0) {
      setIsDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedClipIds.length === 0) return;
    
    setIsDeleting(true);
    try {
      await deleteClips(selectedClipIds);
      
      // Remove deleted clips from local state
      setClips(prev => prev.filter(clip => !selectedClipIds.includes(clip.id)));
      setSelectedClipIds([]);
      setIsDeleteDialogOpen(false);
      setIsEditMode(false);
    } catch (error: any) {
      console.error('Failed to delete clips:', error);
      alert(error.response?.data?.message || 'Failed to delete clips. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter clips based on active category
  const displayedClips = clips.filter(clip => clip.type === activeCategory);

  if (isLoading) {
    return <div className="text-center py-10 text-white">Loading clips...</div>;
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/clips')}
        className="inline-flex items-center mb-6 text-primary hover:text-primary/80 transition-colors"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to VODs
      </button>
      <h1 className="text-3xl font-bold text-white mb-2 truncate" title={vodInfo.title || ''}>
        {vodInfo.title || 'Clips'}
      </h1>
      <p className="text-lg text-muted-foreground mb-6">All clips from this VOD</p>

      {/* Category Selector and Edit Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-center mb-8 gap-4">
        {/* Category Selector */}
        <div className="relative bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-2 shadow-lg">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setActiveCategory('original')}
              className={`
                flex items-center justify-center px-6 py-3 rounded-xl text-sm font-semibold 
                transition-all duration-200 relative z-10 group
                ${activeCategory === 'original' 
                  ? 'bg-gradient-to-r from-blue-500 via-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/25 border border-blue-400/30' 
                  : 'text-white/60 hover:text-white hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-cyan-500/10 hover:border-blue-400/20 border border-transparent'
                }
              `}
            >
              <div className={`p-1.5 rounded-lg mr-2 transition-all duration-200 ${
                activeCategory === 'original'
                  ? 'bg-white/20 backdrop-blur-sm'
                  : 'bg-white/5 group-hover:bg-white/10'
              }`}>
                <Video size={14} className="transition-transform group-hover:scale-110" />
              </div>
              Raw Clips (16:9)
            </button>
            <button
              onClick={() => setActiveCategory('edited')}
              className={`
                flex items-center justify-center px-6 py-3 rounded-xl text-sm font-semibold 
                transition-all duration-200 relative z-10 group
                ${activeCategory === 'edited' 
                  ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white shadow-lg shadow-purple-500/25 border border-purple-400/30' 
                  : 'text-white/60 hover:text-white hover:bg-gradient-to-r hover:from-purple-500/10 hover:to-pink-500/10 hover:border-purple-400/20 border border-transparent'
                }
              `}
            >
              <div className={`p-1.5 rounded-lg mr-2 transition-all duration-200 ${
                activeCategory === 'edited'
                  ? 'bg-white/20 backdrop-blur-sm'
                  : 'bg-white/5 group-hover:bg-white/10'
              }`}>
                <Wand2 size={14} className="transition-transform group-hover:scale-110" />
              </div>
              Edited Clips (9:16)
            </button>
          </div>
        </div>

        {/* Edit Mode Controls */}
        {displayedClips.length > 0 && (
          <div className="flex items-center space-x-3">
            {isEditMode && (
              <>
                {/* Select All/None Toggle */}
                <button
                  onClick={selectedClipIds.length === displayedClips.length ? deselectAllClips : selectAllClips}
                  className="flex items-center px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm backdrop-blur-sm border border-white/10"
                >
                  {selectedClipIds.length === displayedClips.length ? (
                    <CheckSquare size={16} className="mr-2" />
                  ) : (
                    <Square size={16} className="mr-2" />
                  )}
                  {selectedClipIds.length === displayedClips.length ? 'Deselect All' : 'Select All'}
                </button>

                {/* Delete Button */}
                <button
                  onClick={handleDeleteClick}
                  disabled={selectedClipIds.length === 0}
                  className="flex items-center px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm border border-red-400/20"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete ({selectedClipIds.length})
                </button>
              </>
            )}

            {/* Edit Mode Toggle */}
            <button
              onClick={toggleEditMode}
              className={`
                flex items-center px-4 py-2 rounded-lg font-medium transition-colors text-sm backdrop-blur-sm border
                ${isEditMode 
                  ? 'bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 border-gray-400/20' 
                  : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border-blue-400/20'
                }
              `}
            >
              <Edit3 size={16} className="mr-2" />
              {isEditMode ? 'Cancel' : 'Edit'}
            </button>
          </div>
        )}
      </div>

      {clips.length > 0 ? (
        displayedClips.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {displayedClips.map((clip) => (
              <ClipCard
                key={clip.id}
                id={clip.id}
                title={clip.title}
                thumbnailPath={clip.thumbnailPath}
                filePath={clip.filePath}
                jobId={clip.jobId}
                llmOutputId={clip.llmOutputId}
                type={clip.type}
                onCardClick={() => handleClipClick(clip)}
                isSelected={isEditMode ? selectedClipIds.includes(clip.id) : undefined}
                onSelect={isEditMode ? handleClipSelection : undefined}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-gray-500/20 to-slate-500/20 rounded-2xl opacity-30 blur-xl" />
              
              <div className="relative bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
                <div className="flex flex-col items-center text-center">
                  <div className={`p-4 rounded-xl mb-4 transition-all duration-300 ${
                    activeCategory === 'original'
                      ? 'bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-400/20'
                      : 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-400/20'
                  }`}>
                    {activeCategory === 'original' 
                      ? <Video size={32} className="text-blue-400" /> 
                      : <Wand2 size={32} className="text-purple-400" />
                    }
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    No {activeCategory === 'original' ? 'raw' : 'edited'} clips found
                  </h3>
                  <p className="text-white/60">
                    {activeCategory === 'original' 
                      ? 'This VOD has no AI-generated raw clips yet'
                      : 'No clips have been edited for this VOD yet'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      ) : (
        <p className="text-center py-10 text-gray-400">No clips found for this VOD.</p>
      )}

      {selectedClip && (
        <ClipDetailPopup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          title={selectedClip.title}
          clipId={selectedClip.id}
          videoSrc={selectedClip.filePath}
          jobId={selectedClip.jobId}
          llmOutputId={selectedClip.llmOutputId}
          clipType={selectedClip.type}
          allClips={displayedClips}
          currentClipIndex={displayedClips.findIndex(c => c.id === selectedClip.id)}
          onNavigate={handleNavigate}
          duration={selectedClip.duration}
          createdAt={selectedClip.createdAt}
          vodTitle={selectedClip.vodTitle}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        clipCount={selectedClipIds.length}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default ClipListPage;